import { useEffect } from 'react';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import useTranslation from 'pages/ApplicationDetailsPage/hooks/useTranslation';

import useData from './hooks/useData';

import FlightPlannerPanelHeader from './components/FlightPlannerPanelHeader';
import FlightPlannerSections from './components/FlightPlannerSections';
import FlightPlannerStats from './components/FlightPlannerStats';
import PlanWarnings from './components/PlanWarnings';
import PrescriptionSection from './components/PrescriptionSection';
import SendToDroneControl from './components/SendToDroneControl';
import { SECTION_ADVANCED, SECTION_COVERAGE, SECTION_SPRAY, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const FlightPlannerPanel: FunctionComponent<Props> = ({
    application,
    boundary,
    className = '',
    isSendingToDrone,
    onClose,
    onFlightPathsChange,
    onPrescriptionChange,
    onSaved,
    onSendToDrone,
    onSessionIdChange,
    sendToDroneResultText,
    testId = TEST_ID
}) => {
    const { SEND_TO_DRONE, SEND_TO_DRONE_SENDING } = useTranslation();
    const data = useData({ application, boundary, onClose, onPrescriptionChange, onSaved });
    const {
        boundaryAreaHectares,
        config,
        flightPaths,
        handleAdvancedToggle,
        handleConfigChange,
        handleCoverageToggle,
        handleDiscard,
        handleHomePointPick,
        handleSave,
        handleSpraySettingsChange,
        handleSprayToggle,
        isPickingHomePoint,
        isSaving,
        planResult,
        sectionOpenState,
        sessionId,
        spraySettings
    } = data;
    const sendToDroneLabel = isSendingToDrone ? SEND_TO_DRONE_SENDING : SEND_TO_DRONE;

    useEffect(() => {
        onFlightPathsChange({ flightPaths });
    }, [flightPaths, onFlightPathsChange]);

    // Surface the planner session id to the outer useData so
    // useFlightSimulation can POST /api/sessions/{id}/sendToDrone against
    // a stable session. The outer hook lives one level up (it persists
    // across isPlanning transitions) so sim state stays in memory after
    // the planner closes.
    useEffect(() => {
        onSessionIdChange(sessionId);
    }, [onSessionIdChange, sessionId]);

    return (
        <VerticalContainer className={`${className} ${styles.flightPlannerPanel}`} testId={testId}>
            <FlightPlannerPanelHeader
                isSaving={isSaving}
                onDiscard={handleDiscard}
                onSave={handleSave}
            />

            <PlanWarnings warnings={planResult?.warnings ?? []} />

            <FlightPlannerSections
                config={config}
                isAdvancedOpen={sectionOpenState[SECTION_ADVANCED]}
                isCoverageOpen={sectionOpenState[SECTION_COVERAGE]}
                isHomePointPickDisabled={false}
                isPickingHomePoint={isPickingHomePoint}
                isSprayOpen={sectionOpenState[SECTION_SPRAY]}
                onAdvancedToggle={handleAdvancedToggle}
                onConfigChange={handleConfigChange}
                onCoverageToggle={handleCoverageToggle}
                onPickHomePoint={handleHomePointPick}
                onSpraySettingsChange={handleSpraySettingsChange}
                onSprayToggle={handleSprayToggle}
                spraySettings={spraySettings}
            />

            <PrescriptionSection
                attachError={data.attachError}
                attachPrescription={data.attachPrescription}
                detachPrescription={data.detachPrescription}
                isAttachingPrescription={data.isAttachingPrescription}
                isDetachingPrescription={data.isDetachingPrescription}
                onReplan={data.handlePrescriptionReplan}
                rateBounds={data.rateBounds}
                zoneCount={data.zoneCount}
            />

            <SendToDroneControl
                isDisabled={isSendingToDrone || !planResult}
                label={sendToDroneLabel}
                onClick={onSendToDrone}
                resultText={sendToDroneResultText}
            />

            <FlightPlannerStats
                boundaryAreaHectares={boundaryAreaHectares}
                speedMetersPerSecond={spraySettings.speedMetersPerSecond}
                stats={planResult?.stats ?? null}
            />
        </VerticalContainer>
    );
};

export default FlightPlannerPanel;
